import { useEffect, type ReactElement } from "react";
import { useSearchParams } from "react-router";

import { isLocalPath } from "../../api/client.js";
import { useApiData } from "../../api/use-api-data.js";
import { queryValue } from "../../app/navigation.js";
import { loginUrlAfterUnauthorized, registerUrlAfterUnauthorized } from "../../app/session-expiry.js";
import { useGo } from "../../app/use-go.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { FormSummary } from "../../forms/fields.js";
import { useApiForm } from "../../forms/use-api-form.js";

import { DataPending } from "./data-pending.js";
import { accountRegisterUrl, invitationPassphrase, tenantUserUrls } from "./urls.js";

/** 承諾できる招待（サーバの TenantUserRegisterViewModel と同じ欄）。 */
export interface TenantUserInvitation {
    readonly tenantId: number;
    readonly tenantTitle: string;
    readonly passphrase: string;
    readonly userName: string;
}

/**
 * 招待リンクを開いたとき（<c>GET /api/tenants/{契約}/users/register</c>。サーバの ApiInvitationResponse）。
 * <b>どちらか一方が入る</b>——承諾できる招待か、別の画面への行き先（承諾済み・別の人がログイン中）。
 */
export interface InvitationAnswer {
    readonly invitation: TenantUserInvitation | null;
    readonly location: string | null;
}

/**
 * 未ログインで開いたときの行き先。<b>ログインではなく、合言葉を引き継いだアカウント登録の画面へ直接送る</b>
 * （Implem.Portal.Frontend の #293）。戻り先にはこの画面（招待のリンク）を付け、登録の画面の「ログイン」から戻れるようにする。
 * <b>合言葉の無い URL は今までどおりログインへ</b>（引き継ぐものが無い）。
 */
const toAccountRegister = (returnTo: string): string =>
    invitationPassphrase(returnTo) === undefined
        ? loginUrlAfterUnauthorized(returnTo, false)
        : registerUrlAfterUnauthorized(accountRegisterUrl(returnTo));

function RegisterForm({ invitation }: { readonly invitation: TenantUserInvitation }): ReactElement {
    // 画面の URL の合言葉（?passphrase=）も一緒に渡る（useApiForm がクエリを添える）
    const form = useApiForm(tenantUserUrls.api.register(invitation.tenantId));

    return (
        <form method="post" onSubmit={form.onSubmit}>
            <FormSummary messages={form.summary} />
            <input type="hidden" name="passphrase" value={invitation.passphrase} />
            <button type="submit" className="btn-primary w-full py-3" disabled={form.busy}>承諾してはじめる</button>
        </form>
    );
}

/**
 * 招待の承諾。<b>招待メールのリンクがそのまま開く画面</b>（<c>InvitationLink</c>）。
 * 承諾した後の行き先（プリザンター）もサーバが決める。
 */
export function RegisterPage({ tenantId }: { readonly tenantId: number }): ReactElement {
    usePageMeta("ご招待の承諾");
    const [search] = useSearchParams();
    const go = useGo();
    const data = useApiData<InvitationAnswer>(
        tenantUserUrls.api.register(tenantId, queryValue(search, "passphrase") ?? ""), toAccountRegister);

    const location = data.state === "ready" ? data.value.location : null;
    useEffect(() => {
        // 行き先を決めるのはサーバだが、受け取った値をそのまま信じない（外のサイトへ出さない）
        if (location !== null && isLocalPath(location)) {
            go(location);
        }
    }, [location, go]);

    if (data.state !== "ready") {
        return <DataPending data={data} />;
    }

    const { invitation } = data.value;
    if (invitation === null) {
        // 移っている間は読み込み中のまま。行き先が無ければ開けなかったことを伝える
        return <DataPending data={location !== null && isLocalPath(location) ? { state: "loading" } : { state: "failed", at: new Date() }} />;
    }

    return (
        <div className="page max-w-lg">
            <div className="panel">
                <h1 className="page-title">ご招待の承諾</h1>
                <p className="page-lead">
                    <span className="font-medium text-slate-900">{invitation.tenantTitle}</span> へご招待されています。<br />
                    承諾すると、サービスをご利用いただけるようになります。
                </p>

                <dl className="mb-7 grid grid-cols-[5rem_1fr] gap-y-2 text-sm">
                    <dt className="text-slate-500">お名前</dt>
                    <dd>{invitation.userName}</dd>
                </dl>

                <RegisterForm invitation={invitation} />
            </div>
        </div>
    );
}
